import { Controller } from "@hotwired/stimulus"

// Controller for managing the purchase summary sidebar
// Listens for donation add/remove/update events and refreshes the summary
export default class extends Controller {
  static targets = [
    "donationsSection",
    "donationsSubtotal",
    "feeCoverage",
    "grandTotal",
    "subtotal",
    "feeCoverageCheckbox",
    "feeCoveragePreview"
  ]

  static values = {
    cartId: String,
    updateFeeCoverageUrl: { type: String, default: "/shopping_cart/update_fee_coverage" }
  }

  connect() {
    console.log("Purchase Summary Controller connected")

    // Bind event handlers
    this.boundHandleDonationUpdated = this.handleDonationUpdated.bind(this)
    this.boundHandleDonationRemoved = this.handleDonationRemoved.bind(this)
    this.boundHandleDonationAdded = this.handleDonationAdded.bind(this)
    this.boundHandleCartUpdated = this.handleCartUpdated.bind(this)

    // Listen for donation events from org-donation and organization-selector controllers
    // Stimulus dispatch creates events like "org-donation:donationUpdated" that bubble up
    document.addEventListener('org-donation:donationUpdated', this.boundHandleDonationUpdated)
    document.addEventListener('org-donation:donationRemoved', this.boundHandleDonationRemoved)
    document.addEventListener('organization-selector:donationAdded', this.boundHandleDonationAdded)
    // Listen for cart item updates from cart-item controller
    document.addEventListener('cartUpdated', this.boundHandleCartUpdated)
  }

  disconnect() {
    document.removeEventListener('org-donation:donationUpdated', this.boundHandleDonationUpdated)
    document.removeEventListener('org-donation:donationRemoved', this.boundHandleDonationRemoved)
    document.removeEventListener('organization-selector:donationAdded', this.boundHandleDonationAdded)
    document.removeEventListener('cartUpdated', this.boundHandleCartUpdated)
  }

  handleDonationAdded(event) {
    console.log("Purchase Summary: Donation added", event.detail)
    const { donation, cartTotal, organizationName } = event.detail

    // Add a new donation row to the summary
    this.addDonationRow(donation, organizationName)

    // Update totals
    this.refreshTotals()
  }

  handleDonationRemoved(event) {
    console.log("Purchase Summary: Donation removed", event.detail)
    const { itemId, orgId, cartTotal } = event.detail

    // Remove the donation row from the summary
    this.removeDonationRow(itemId, orgId)

    // Update totals after a short delay to ensure the org-donation element
    // has been removed from the DOM (the event is dispatched before removal)
    console.log("Purchase Summary: Scheduling refreshTotals after donation removal")
    setTimeout(() => {
      console.log("Purchase Summary: Calling refreshTotals after donation removal")
      this.refreshTotals()
    }, 100)
  }

  handleDonationUpdated(event) {
    console.log("Purchase Summary: Donation updated", event.detail)
    const { itemId, donation, cartTotal } = event.detail

    // Update the donation amount in the summary
    this.updateDonationRow(donation)

    // Update totals
    this.refreshTotals()
  }

  handleCartUpdated(event) {
    console.log("Purchase Summary: Cart updated", event.detail)
    // Update totals from server response if available
    if (event.detail && event.detail.cart) {
      this.updateFromCartData(event.detail.cart)
    } else {
      this.refreshTotals()
    }
  }

  toggleFeeCoverage(event) {
    const isEnabled = event.target.checked
    console.log("Purchase Summary: Toggling fee coverage to", isEnabled)

    fetch(this.updateFeeCoverageUrlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        fee_coverage_enabled: isEnabled
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log("Purchase Summary: Fee coverage update response", data)
      if (data.success) {
        this.updateFromCartData(data.cart)

        // Show/hide the fee line
        const feeLine = document.getElementById('fee-line')
        if (feeLine) {
          feeLine.style.display = isEnabled ? '' : 'none'
        }

        // Update the fee preview in the checkbox label
        if (this.hasFeeCoveragePreviewTarget && data.cart.fee_coverage_amount !== undefined) {
          const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
          this.feeCoveragePreviewTarget.textContent = formatter.format(data.cart.fee_coverage_amount)
        }

        // Dispatch cartUpdated event so other controllers know
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: data }))
      } else {
        console.error("Fee coverage update failed:", data.error)
        // Revert checkbox on error
        event.target.checked = !isEnabled
      }
    })
    .catch(error => {
      console.error("Error updating fee coverage:", error)
      // Revert checkbox on error
      event.target.checked = !isEnabled
    })
  }

  updateFromCartData(cart) {
    console.log("Purchase Summary: updateFromCartData called with:", cart)
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

    console.log("Purchase Summary: hasGrandTotalTarget:", this.hasGrandTotalTarget)
    console.log("Purchase Summary: hasFeeCoverageTarget:", this.hasFeeCoverageTarget)
    console.log("Purchase Summary: hasDonationsSubtotalTarget:", this.hasDonationsSubtotalTarget)

    if (this.hasGrandTotalTarget && cart.total !== undefined) {
      console.log("Purchase Summary: Updating grandTotal to", cart.total)
      this.grandTotalTarget.textContent = formatter.format(cart.total)
    } else {
      console.log("Purchase Summary: Cannot update grandTotal - hasTarget:", this.hasGrandTotalTarget, "total:", cart.total)
    }

    if (this.hasFeeCoverageTarget && cart.fee_coverage_amount !== undefined) {
      console.log("Purchase Summary: Updating feeCoverage to", cart.fee_coverage_amount)
      this.feeCoverageTarget.textContent = formatter.format(cart.fee_coverage_amount)
    } else {
      console.log("Purchase Summary: Cannot update feeCoverage - hasTarget:", this.hasFeeCoverageTarget, "amount:", cart.fee_coverage_amount)
    }

    if (this.hasDonationsSubtotalTarget && cart.donation_amount !== undefined) {
      console.log("Purchase Summary: Updating donationsSubtotal to", cart.donation_amount)
      this.donationsSubtotalTarget.textContent = formatter.format(cart.donation_amount)

      // Show/hide donation amount row based on whether there's a donation
      const donationRow = document.getElementById('donation-amount-row')
      if (donationRow) {
        donationRow.style.display = cart.donation_amount > 0 ? '' : 'none'
      }
    } else {
      console.log("Purchase Summary: Cannot update donationsSubtotal - hasTarget:", this.hasDonationsSubtotalTarget, "amount:", cart.donation_amount)
    }

    // Also update the subtotal (items total) if we have a target for it
    if (this.hasSubtotalTarget && cart.subtotal !== undefined) {
      console.log("Purchase Summary: Updating subtotal to", cart.subtotal)
      this.subtotalTarget.textContent = formatter.format(cart.subtotal)
    }

    // Update the fee preview in the checkbox label
    if (this.hasFeeCoveragePreviewTarget && cart.fee_coverage_amount !== undefined) {
      this.feeCoveragePreviewTarget.textContent = formatter.format(cart.fee_coverage_amount)
    }

    // Show/hide fee line based on fee coverage enabled status
    const feeLine = document.getElementById('fee-line')
    if (feeLine && cart.fee_coverage_enabled !== undefined) {
      feeLine.style.display = cart.fee_coverage_enabled ? '' : 'none'
    }
  }

  addDonationRow(donation, organizationName) {
    if (!this.hasDonationsSectionTarget) return

    const amount = parseFloat(donation.amount) || 0
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

    const rowHTML = `
      <div class="donation-row d-flex justify-content-between align-items-start mb-2"
           data-donation-item-id="${donation.id}"
           data-donation-org-id="${donation.organization_id}">
        <div class="donation-info">
          <div class="donation-org fw-bold">${this.escapeHtml(organizationName)}</div>
          <div class="donation-label text-muted small"><span class="donation-label-text">Donation Amount</span></div>
        </div>
        <div class="donation-amount" data-donation-amount-for-org="${donation.organization_id}">
          ${formatter.format(amount)}
        </div>
      </div>
    `

    this.donationsSectionTarget.insertAdjacentHTML('beforeend', rowHTML)
  }

  removeDonationRow(itemId, orgId) {
    // Try to find by item ID first, then by org ID
    let row = this.element.querySelector(`[data-donation-item-id="${itemId}"]`)
    if (!row && orgId) {
      row = this.element.querySelector(`[data-donation-org-id="${orgId}"]`)
    }

    if (row) {
      row.remove()
    }
  }

  updateDonationRow(donation) {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    const amount = parseFloat(donation.amount) || 0

    // Find the amount element for this organization
    const amountEl = this.element.querySelector(`[data-donation-amount-for-org="${donation.organization_id}"]`)
    if (amountEl) {
      amountEl.textContent = formatter.format(amount)
    }
  }

  refreshTotals() {
    // Calculate totals from all org donation controllers on the page
    let totalDonations = 0
    let totalFeeCoverage = 0

    const orgDonationElements = document.querySelectorAll('[data-controller~="org-donation"]')
    console.log("Purchase Summary refreshTotals: Found", orgDonationElements.length, "org-donation elements")

    orgDonationElements.forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(el, 'org-donation')
      if (controller) {
        const amount = controller.donationAmount || 0
        totalDonations += amount

        // Check if fee coverage is enabled for this org
        if (controller.hasFeeCoverageTarget && controller.feeCoverageTarget.checked) {
          const orgFee = (amount * 0.05) + 0.30
          const orgFeeCoverage = orgFee / (1 - 0.05)
          totalFeeCoverage += orgFeeCoverage
        }
      }
    })

    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

    if (this.hasDonationsSubtotalTarget) {
      this.donationsSubtotalTarget.textContent = formatter.format(totalDonations)
    }

    if (this.hasFeeCoverageTarget) {
      this.feeCoverageTarget.textContent = formatter.format(totalFeeCoverage)
    }

    if (this.hasGrandTotalTarget) {
      const grandTotal = totalDonations + totalFeeCoverage
      this.grandTotalTarget.textContent = formatter.format(grandTotal)
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
